import { Client, UserCredentials } from '@botpress/webchat-client'
import { createEventEmitter, EventEmitter } from '@bpinternal/shared'
import { useEffect, useMemo, useRef, useState } from 'react'
import { integrationMessageToBlockMessage } from '../adapters'
import { type Events } from '../events'
import { initializeConversation } from '../signals/initialize'
import { getUseMessagesStore, getUseParticipantStore, getUseWebchatClientStore } from '../stores'
import { type StorageLocation, type WebchatError } from '../types'
import { checkConfiguration, mapToWebchatError, resolveInitParams } from '../utils'
import { debug } from '../utils/debug'
import { useTimeoutState } from './useTimeoutState'

const log = debug.webchat

type ClientStates = 'connecting' | 'connected' | 'error' | 'disconnected'

export type UseInitializationProps = {
  apiUrl?: string
  clientId: string
  storageKey?: string
  storageLocation?: StorageLocation
  adminSecret?: string
  clientHeaders?: Record<string, string>
  conversationId?: string
  userToken?: string
  playNotificationSound?: () => void
}

export type UseInitializationReturn = {
  // State
  clientState: ClientStates
  httpClient: Client | undefined
  error: WebchatError | undefined
  activeConversationId: string | undefined
  userCredentials: UserCredentials | undefined
  isTyping: boolean
  isAwaitingResponse: boolean

  // Controls
  openConversation: (params: { conversationId?: string; userToken?: string }) => Promise<void>
  setAwaitingResponse: (state: boolean, timeout?: number) => void
  emitter: EventEmitter<Events>

  // Setters (for error recovery in useWebchat)
  setError: (error: WebchatError | undefined) => void
  setClientState: (state: ClientStates) => void
}

/**
 * @internal
 * @deprecated This hook is temporary and will be removed when useWebchat is fully deprecated.
 * It exists only to support the deprecated useWebchat hook and the new WebchatProvider.
 * Move this code into the WebchatProvider when useWebchat is deleted
 */
export function useInitialization({
  apiUrl = 'https://webchat.botpress.cloud',
  clientId,
  storageKey,
  storageLocation,
  adminSecret,
  clientHeaders,
  playNotificationSound,
  ...props
}: UseInitializationProps): UseInitializationReturn {
  const useWebchatClientStore = getUseWebchatClientStore(storageKey, storageLocation)

  const activeConversationId = useWebchatClientStore((state) => state.conversationId)
  const userCredentials = useWebchatClientStore((state) => state.user)
  const setActiveConversationId = useWebchatClientStore((state) => state.setConversationId)
  const setUserCredentials = useWebchatClientStore((state) => state.setUser)

  const { state: isTyping, setState: setIsTyping } = useTimeoutState()
  const { state: isAwaitingResponse, setState: setAwaitingResponse } = useTimeoutState()
  const emitter = useMemo(() => createEventEmitter<Events>(), [])

  const [clientState, setClientState] = useState<ClientStates>('connecting')
  const [httpClient, setHttpClient] = useState<Client | undefined>(undefined)
  const [error, setError] = useState<WebchatError | undefined>(undefined)

  const eventCleanup = useRef<(() => void) | null>(null)

  const initParams = resolveInitParams(
    props.conversationId,
    props.userToken,
    activeConversationId,
    userCredentials?.userToken
  )

  useEffect(() => {
    let cancelled = false
    log.info('starting initialization', { clientId, apiUrl, storageKey })

    const initAsync = async () => {
      await openConversation(initParams)

      if (cancelled) {
        log.debug('initialization completed but effect was cleaned up, tearing down')
        eventCleanup.current?.()
        eventCleanup.current = null
      }
    }

    const configError = checkConfiguration(clientId, apiUrl)

    if (configError) {
      log.error('configuration error', configError)
      setError(configError)
      setClientState('error')
      return
    }

    initAsync()

    return () => {
      cancelled = true
      if (eventCleanup.current) {
        eventCleanup.current()
        eventCleanup.current = null
      }
    }
  }, [props.conversationId, props.userToken, clientId, apiUrl])

  const openConversation = async ({ conversationId, userToken }: { conversationId?: string; userToken?: string }) => {
    eventCleanup.current?.()

    setError(undefined)
    setIsTyping(false)
    setClientState('connecting')

    const { on, conversation, messages, participants, user, client, onCleanup } = await initializeConversation({
      apiUrl,
      clientId,
      clientHeaders,
      emitter,
      adminSecret,
      conversationId,
      userToken,
    })

    setHttpClient(client)
    setActiveConversationId(conversation.id)
    setUserCredentials({ userId: user.id, userToken: user.userKey })

    const messagesStore = getUseMessagesStore(conversation.id)
    const blockMessages = messages.reverse().map(integrationMessageToBlockMessage).filter(Boolean)
    messagesStore.getState().setMessages(blockMessages)

    const participantsStore = getUseParticipantStore(conversation.id)
    participantsStore.getState().setParticipants(participants)

    const unsubs = [
      onCleanup,
      on('error', (err) => {
        log.error('connection error', { error: err, conversationId: conversation.id })
        setError((prev) => prev || mapToWebchatError(err))
        setClientState('error')
        emitter.emit('error', new Error(`Connection to conversation lost: ${err?.message}`))
      }),
      on('message_created', (ev) => {
        const blockMessage = integrationMessageToBlockMessage(ev)
        messagesStore.getState().saveMessage(blockMessage)

        if (user.id !== blockMessage.authorId) {
          playNotificationSound?.()
          setIsTyping(false)
          setAwaitingResponse(false)
          emitter.emit('isTyping', { isTyping: false, timeout: 0 })
        }
        emitter.emit('message', blockMessage)
      }),
      on('message_status_changed', (ev) => {
        if (ev.newStatus === 'processing') {
          messagesStore.getState().updateMessage(ev.message.metadata?.clientMessageId, {
            status: ev.newStatus as 'pending' | 'processing' | 'processed' | 'failed' | 'skipped',
          })
        }
      }),
      on('typing_stopped', () => {
        setIsTyping(false)
        setAwaitingResponse(false)
      }),
      on('typing_started', () => {
        setIsTyping(true, 5000)
        setAwaitingResponse(true, 5000)
        emitter.emit('isTyping', { isTyping: true, timeout: 5000 })
      }),
      on('participant_added', (ev) => {
        participantsStore.getState().addParticipant(ev.participant)
        emitter.emit('participantAdded', ev.participant)
      }),
      on('participant_removed', (ev) => {
        participantsStore.getState().removeParticipant(ev.participantId)
        emitter.emit('participantRemoved', ev.participantId)
      }),
      on('webchat_visibility', (ev) => emitter.emit('webchatVisibility', ev.visibility)),
      on('webchat_config', (ev) => emitter.emit('webchatConfig', ev.config)),
      on('custom', (ev) => emitter.emit('customEvent', ev.event)),
    ]

    setClientState('connected')
    log.info('client fully connected with event handlers ready', { conversationId: conversation.id })

    eventCleanup.current = () => {
      unsubs.forEach((unsub) => unsub())
    }
  }

  return {
    clientState,
    httpClient,
    error,
    activeConversationId,
    userCredentials,
    isTyping,
    isAwaitingResponse,
    openConversation,
    setAwaitingResponse,
    emitter,
    setError,
    setClientState,
  }
}
